import { Check, DollarSign, Plus } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Product ownership pill for whitespace/attach tables. Three states:
 *
 *   billed     → solid pill with a $ — the item is on the customer's invoice
 *   contracted → outlined pill with a ✓ — on an active contract, not billing
 *   none       → dashed "+" cell — open whitespace (the upsell target)
 *
 * `color` is a DS palette token; the mapping is a static lookup so Tailwind
 * can see every class. Used by Upsell Ops; reach for this instead of rolling
 * per-page ownership cells.
 */
export type ProductPillState = "billed" | "contracted" | "none";
export type ProductPillColor =
  | "brand"
  | "power"
  | "purple"
  | "success"
  | "orange"
  | "teal";

const SOLID: Record<ProductPillColor, string> = {
  brand: "bg-brand text-white",
  power: "bg-power text-power-deep",
  purple: "bg-purple text-white",
  success: "bg-success text-white",
  orange: "bg-orange text-white",
  teal: "bg-teal text-white",
};

const OUTLINE: Record<ProductPillColor, string> = {
  brand: "border-brand text-brand",
  power: "border-power text-power-deep",
  purple: "border-purple text-purple",
  success: "border-success text-success",
  orange: "border-orange text-orange",
  teal: "border-teal text-teal",
};

const STATE_TITLE: Record<ProductPillState, string> = {
  billed: "Billing — on the invoice this month",
  contracted: "On an active contract, not billing yet",
  none: "Whitespace — open upsell",
};

export function ProductStatePill({
  state,
  color = "brand",
  title,
  className,
}: {
  state: ProductPillState;
  color?: ProductPillColor;
  /** Tooltip override; defaults to a state explanation. */
  title?: string;
  className?: string;
}) {
  const base =
    "inline-flex h-6 min-w-[1.9rem] items-center justify-center rounded-md text-xs font-bold";
  if (state === "billed") {
    return (
      <span className={cn(base, SOLID[color], className)} title={title ?? STATE_TITLE.billed}>
        <DollarSign className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (state === "contracted") {
    return (
      <span
        className={cn(base, "border-[1.5px]", OUTLINE[color], className)}
        title={title ?? STATE_TITLE.contracted}
      >
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  return (
    <span
      className={cn(
        base,
        "border-[1.5px] border-dashed border-border text-muted-foreground/50",
        className,
      )}
      title={title ?? STATE_TITLE.none}
    >
      <Plus className="h-3 w-3" />
    </span>
  );
}
